import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Lightbulb, Users, Video, Palette } from 'lucide-react';

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const focusAreas = [
    { icon: <Video className="w-6 h-6" />, text: 'Media production' },
    { icon: <Lightbulb className="w-6 h-6" />, text: 'Digital content creation' },
    { icon: <Palette className="w-6 h-6" />, text: 'Branding and advertising' },
    { icon: <Target className="w-6 h-6" />, text: 'Marketing strategy' },
    { icon: <Users className="w-6 h-6" />, text: 'Online platforms and digital media' },
  ];

  return (
    <div data-testid="about-page" className="min-h-screen pt-32">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-outfit font-bold tracking-tighter text-white mb-6"
          >
            About TriVenza Private Limited
          </motion.h1>
        </div>
      </section>

      {/* Company Overview */}
      <section data-testid="company-overview-section" className="py-16 px-6 md:px-12 lg:px-24 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-outfit font-bold tracking-tighter text-white mb-6">
                Company Overview
              </h2>
              <p className="text-base md:text-lg text-gray-400 font-manrope leading-relaxed">
                TriVenza Private Limited is a media production and digital services company focused on delivering innovative content and digital experiences. We combine creativity with technology to help brands tell their stories and connect with their audiences in meaningful ways.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758873268783-967d13f0c163?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGFnZW5jeSUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3MzA0ODMzNHww&ixlib=rb-4.1.0&q=85"
                alt="Team collaboration"
                className="rounded-3xl border border-white/10 w-full h-auto"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section data-testid="vision-mission-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
          <motion.div
            {...fadeInUp}
            className="bg-[#0f172a]/50 backdrop-blur-sm border border-white/10 rounded-3xl p-10 hover:border-[#7c3aed]/50 transition-all duration-300"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-gradient-to-br from-[#7c3aed] to-[#3b82f6] p-3 rounded-2xl">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-outfit font-bold text-white">Vision</h2>
            </div>
            <p className="text-base md:text-lg text-gray-400 font-manrope leading-relaxed">
              To build a dynamic media and digital solutions company that empowers brands and creators in the evolving digital ecosystem.
            </p>
          </motion.div>

          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.2 }}
            className="bg-[#0f172a]/50 backdrop-blur-sm border border-white/10 rounded-3xl p-10 hover:border-[#3b82f6]/50 transition-all duration-300"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-gradient-to-br from-[#3b82f6] to-[#7c3aed] p-3 rounded-2xl">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-outfit font-bold text-white">Mission</h2>
            </div>
            <p className="text-base md:text-lg text-gray-400 font-manrope leading-relaxed">
              To produce impactful digital content, innovative media experiences, and strategic branding solutions that drive results for our clients.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Focus Areas */}
      <section data-testid="core-focus-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0f172a]">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            {...fadeInUp}
            className="text-4xl md:text-5xl font-outfit font-bold tracking-tighter text-white mb-16 text-center"
          >
            Core Focus Areas
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {focusAreas.map((area, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ delay: index * 0.1 }}
                data-testid={`focus-area-${index}`}
                className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] border border-white/10 rounded-2xl p-8 hover:border-[#7c3aed]/50 hover:shadow-lg hover:shadow-[#7c3aed]/20 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="text-[#7c3aed]">{area.icon}</div>
                  <p className="text-white font-manrope font-medium">{area.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp}>
            <img
              src="https://images.unsplash.com/photo-1758873271902-a63ecd5b5235?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjcmVhdGl2ZSUyMGFnZW5jeSUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3MzA0ODMzNHww&ixlib=rb-4.1.0&q=85"
              alt="Creative team"
              className="rounded-3xl border border-white/10 w-full h-auto"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;